/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AngModule } from '@dm/ang';
import { CORE_CONFIG, CoreModule, HttpService, I18nService } from '@dm/core';

import { AuthModePermissionE } from '../app/common/constants/permission-multi-auth';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CheckToastModule } from './common/components/check-toast/check-toast.module';
import { LogoutTipModule } from './common/components/logout-tip/logout-tip.module';
import { TaskResultModule } from './common/components/task-result/task-result.module';
import { AppHttpService } from './common/services';
import { ThemesService } from './common/services/themes.service';
import { restConfig } from './config';
import { FeatureConfig } from './config/feature';
import { LoginInfoModule } from './feature/auth/login-info/login-info.module';
import { PoweronFailModule } from './feature/auth/poweron-fail/poweron-fail.module';
import { CheckLunModule } from './feature/protection/check-lun/check-lun.module';
import { UnavailableLunModule } from './feature/protection/object/lun/protect-lun/unavailable-lun/unavailable-lun.module';
import { DeploymentModeModule } from './feature/share/deployment-mode/deployment-mode.module';
import { Error404Module } from './feature/share/error-404/error-404.module';
import { ErrorOtherModule } from './feature/share/error-other/error-other.module';
import { HeaderModule } from './feature/share/header/header.module';
import { HeaderLanguageModule } from './feature/share/header/header-language/header-language.module';
import { QualityInspectModule } from './feature/share/quality-inspect/quality-inspect.module';
import { RemoteAssistanceModule } from './feature/share/remote-assistance/remote-assistance.module';
import { SmartAssistantModule } from './feature/share/smart-assistant/smart-assistant.module';
import { VirtualMachineConsoleModule } from './feature/system/smart-container/virtual-machine/virtual-machine-console/virtual-machine-console.module';

CORE_CONFIG.assetsPath = 'assets/core';
CORE_CONFIG.theme = 'aui';
CORE_CONFIG.uploadTimeout = 70000;
CORE_CONFIG.restConfig = restConfig;
CORE_CONFIG.minInterval = 100;
CORE_CONFIG.showPageOnNoData = true;
CORE_CONFIG.saveColumnFilterStatus = true;
CORE_CONFIG.authModePermissions = AuthModePermissionE;

if (FeatureConfig.IsReplaceStrLunWithNamespace) {
    CORE_CONFIG.premissionTransformNameMap.set('lun_W', 'namespace_W');
    CORE_CONFIG.premissionTransformNameMap.set('lun_R', 'namespace_R');
}

// 为LiveUI补充全局属性angConfig
const _window: any = window;
if (!_window.angConfig) {
    _window.angConfig = Object.create(null);
}

// 加载所有assets/svg下的svg文件
const requireAll = (requireContext): any => requireContext.keys().map(requireContext);
const appSvgs = (import.meta as any).webpackContext('../assets/svg', { recursive: false, regExp: /\.svg$/ });
const coreSvgs = (import.meta as any).webpackContext('../../node_modules/@dm/core/assets/svg', { recursive: false, regExp: /\.svg$/ });
requireAll(appSvgs);
requireAll(coreSvgs);

// eslint-disable-next-line @typescript-eslint/no-var-requires
const angSvg = require('../../node_modules/@dm/ang/assets/icons/aui.svg');
document.body.insertAdjacentHTML('beforeend', `<div style="display: none;">${angSvg}</div>`);

/**
 * 初始化加载i18n资源
 * @param i18n i18n服务
 */
export function initI18n(i18n: I18nService): () => Promise<any> {
    // i18n术语替换依赖判断是否为极简模式，这里首次加载i18n时先通过路由判断一次，解决刷新页面时i18n先加载而CORE_CONFIG还未设置的场景。
    if (window.location.pathname.startsWith('/simple')) {
        CORE_CONFIG.isSimpleMode = true;
    }

    return () => i18n.load('/assets/i18n/resource.' + i18n.language + '.json').toPromise();
}

/**
 * 初始化主题样式
 */
export function initTheme(themes: ThemesService): () => Promise<void> {
    return (): Promise<void> => new Promise<void>((resolve) => {
        themes.initTheme();
        resolve();
    });
}

@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AngModule.forRoot(),
        CoreModule.forRoot(),
        AppRoutingModule,
        QualityInspectModule,
        HeaderModule,
        ErrorOtherModule,
        Error404Module,
        PoweronFailModule,
        LogoutTipModule,
        UnavailableLunModule,
        LoginInfoModule,
        FormsModule,
        TaskResultModule,
        CheckLunModule,
        CheckToastModule,
        HeaderLanguageModule,
        SmartAssistantModule,
        RemoteAssistanceModule,
        DeploymentModeModule,
        VirtualMachineConsoleModule,
    ],
    providers: [
        ThemesService,
        {
            provide: LocationStrategy,
            useClass: PathLocationStrategy,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initI18n,
            deps: [I18nService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initTheme,
            deps: [ThemesService],
            multi: true,
        },
        {
            provide: HttpService,
            useClass: AppHttpService,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule { }
